import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import type { HygieneProductionResult } from '@services/api/dashboard/finance.api'
import {
  buildComparisonFooter,
  formatCurrency,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'

export const HYGIENE_DETAIL_METRIC_IDS = [
  'production-per-visit',
  'net-hygiene-production',
  'ar-over-90',
] as const

export type HygieneDetailMetricId = (typeof HYGIENE_DETAIL_METRIC_IDS)[number]

export function pickMetricsInOrder(
  source: DashboardMetricItemData[],
  orderedIds: readonly string[],
): DashboardMetricItemData[] {
  return orderedIds
    .map((id) => source.find((m) => m.id === id))
    .filter((m): m is DashboardMetricItemData => m != null)
}

/** KPI strip for Production per Hygiene visit detail. */
export function buildProductionPerVisitHygieneKpis(
  data: HygieneProductionResult | null,
): DashboardMetricItemData[] {
  const ppv = data?.productionPerHygieneVisit
  const ppvFooter = ppv ? buildComparisonFooter(ppv) : null
  return [
    {
      id: 'production-per-visit',
      leftLabel: 'Production / visit',
      value: ppv ? formatCurrency(ppv.current) : HYGIENE_PLACEHOLDER,
      footer: ppvFooter?.text,
      footerTrend: ppvFooter == null ? undefined : ppvFooter.up ? 'up' : 'down',
    },
  ]
}

/** KPI strip for Net Hygiene production detail. */
export function buildNetHygieneProductionKpis(
  data: HygieneProductionResult | null,
): DashboardMetricItemData[] {
  const net = data?.hygieneProduction
  const netFooter = net ? buildComparisonFooter(net) : null
  return [
    {
      id: 'net-hygiene-production',
      leftLabel: 'Net hygiene production',
      value: net ? formatCurrency(net.current) : HYGIENE_PLACEHOLDER,
      footer: netFooter?.text,
      footerTrend: netFooter == null ? undefined : netFooter.up ? 'up' : 'down',
    },
  ]
}

const HYGIENE_PLACEHOLDER_LABELS: Record<HygieneDetailMetricId, string> = {
  'net-hygiene-production': 'Net hygiene production',
  'production-per-visit': 'Production / visit',
  'ar-over-90': 'AR over 90 days',
}

/** Placeholder KPIs not yet provided by dashboard-module. */
export function buildHygienePlaceholderKpis(
  ids: readonly HygieneDetailMetricId[],
): DashboardMetricItemData[] {
  return ids.map((id) => ({
    id,
    leftLabel: HYGIENE_PLACEHOLDER_LABELS[id],
    value: HYGIENE_PLACEHOLDER,
  }))
}

/** Merges API-backed KPIs with placeholders for metrics the endpoint does not yet return. */
export function buildHygieneDetailMetrics(
  data: HygieneProductionResult | null,
  orderedIds: readonly HygieneDetailMetricId[] = HYGIENE_DETAIL_METRIC_IDS,
): DashboardMetricItemData[] {
  const fromApi = [
    ...buildProductionPerVisitHygieneKpis(data),
    ...buildNetHygieneProductionKpis(data),
  ]
  const apiIds = new Set(fromApi.map((m) => m.id))
  const placeholderIds = orderedIds.filter(
    (id): id is HygieneDetailMetricId => !apiIds.has(id),
  )
  return pickMetricsInOrder(
    [...fromApi, ...buildHygienePlaceholderKpis(placeholderIds)],
    orderedIds,
  )
}
